import { HabitSchedule, ScheduleChangeMode, Timestamp } from '@griply/types';
import { subDays } from 'date-fns';

import { hasScheduleDefinitionChanged } from './has-schedule-definition-changed';

export enum ScheduleSubmitAction {
  Direct = 'direct',
  ConfirmChanges = 'confirm-changes',
}

export type ScheduleSubmitResolution = { action: ScheduleSubmitAction };

// The "When should this change apply?" prompt is only meaningful when the user
// changes how the habit is defined — recurrence (rrule), frequency, or
// count-per-day — on the currently-active period that already has tracked
// history. Time, dates, a non-active period, or no history all apply directly.
export const resolveScheduleSubmit = (
  initial: HabitSchedule,
  updated: HabitSchedule,
  isActive: boolean,
): ScheduleSubmitResolution => {
  const hasEntries = !!initial.entries?.length;
  if (!isActive || !hasEntries) {
    return { action: ScheduleSubmitAction.Direct };
  }

  return {
    action: hasScheduleDefinitionChanged(initial, updated)
      ? ScheduleSubmitAction.ConfirmChanges
      : ScheduleSubmitAction.Direct,
  };
};

export type ScheduleSplitResult = {
  schedule: HabitSchedule;
  splitOptions?: { originalScheduleEndDate: Date };
};

// "From today" → split: the new period starts today (history reset), and the
// caller caps the original period at yesterday so its entries stay put.
// "From the start" (Original) → replace the period in place; entries untouched.
export const applyScheduleChangeMode = (
  schedule: HabitSchedule,
  mode: ScheduleChangeMode,
  today: Timestamp,
): ScheduleSplitResult => {
  if (mode === ScheduleChangeMode.Today) {
    const resetSchedule: HabitSchedule = {
      ...schedule,
      startDate: today,
      entries: [],
      completions: [],
      skips: [],
    };
    return {
      schedule: resetSchedule,
      splitOptions: { originalScheduleEndDate: subDays(today, 1) },
    };
  }

  return { schedule };
};
